/**
 * Shared file utility functions for handling mobile file URIs.
 */

/**
 * Normalize a file URI by decoding URL-encoded characters in the path portion only.
 * Mobile platforms may return file:// URIs with encoded spaces (%20) and other
 * special characters. RNFS.readFile needs that path decoded.
 *
 * Android document-picker URIs are content:// and already encoded correctly.
 * The document id lives in one path segment (`primary%3ADownload%2Ffile.pdf`).
 * Decoding `%2F` splits that id, ContentResolver throws IllegalArgumentException,
 * and react-native-fs reports it with a null rejection code — which hard-crashes
 * on React Native 0.81 (`PromiseImpl.reject`, parameter `code`). Pass content://
 * URIs through unchanged. Uri.getPathSegments() decodes each segment itself.
 *
 * Query parameters and fragments on file:// URIs are preserved so encoded values
 * there are not corrupted.
 */
export const normalizeFileUri = (uri: string): string => {
  if (!uri.includes('%') || uri.startsWith('content://')) {
    return uri;
  }

  try {
    const queryIndex = uri.indexOf('?');
    const fragmentIndex = uri.indexOf('#');
    let pathEndIndex = uri.length;
    if (
      queryIndex !== -1 &&
      (fragmentIndex === -1 || queryIndex < fragmentIndex)
    ) {
      pathEndIndex = queryIndex;
    } else if (fragmentIndex !== -1) {
      pathEndIndex = fragmentIndex;
    }
    const pathPortion = uri.substring(0, pathEndIndex);
    const suffix = uri.substring(pathEndIndex);
    const decodedPath = decodeURIComponent(pathPortion);
    return decodedPath + suffix;
  } catch {
    console.warn('Failed to decode file URI, using original');
    return uri;
  }
};
